<template>
  <VueSelect v-bind="$attrs" class="m-select-input">
    <template #spinner="{ loading }">
      <div v-if="loading" class="m-select-input__loader">
        <MIcon icon="loader" block size="lg" />
      </div>
    </template>
    <template #no-options="{ search }">
      <div class="m-select-input__error">
        <strong>{{ computedErrorTitle }} "{{ search }}"</strong><br />
        {{ computedErrorMessage }}
      </div>
    </template>
    <!-- Pass on all slots -->
    <template v-for="(_, name) in $slots" #[name]="slotData">
      <slot :name="name" v-bind="slotData" />
    </template>
  </VueSelect>
</template>

<script>
import { computed, defineComponent } from 'vue'
import VueSelect from 'vue-select'

import useI18n from '../../../composables/useI18n'
import MIcon from '../../typography/Icon/MIcon'

import enGB from './locales/en-GB.json'
import enUS from './locales/en-US.json'
import sv from './locales/sv.json'

/**
 * Visual wrapper for [Vue Select](https://vue-select.org/).
 *
 * Passes all props/listeners through; [see documentation](https://vue-select.org/api/props.html).
 * Currently officially supports single selection only.
 */
export default defineComponent({
  name: 'MSelectInput',
  components: {
    MIcon,
    VueSelect,
  },
  i18n: {
    messages: {
      sv,
      'en-US': enUS,
      'en-GB': enGB,
    },
  },
  props: {
    /** Optional 'not found' title which overrides the default */
    errorTitle: {
      type: String,
      default: '',
    },
    /** Optional 'not found' message which overrides the default */
    errorMessage: {
      type: String,
      default: '',
    },
  },
  setup(props) {
    const { t } = useI18n()

    const computedErrorTitle = computed(
      () => props.errorTitle || t('MSelectInput.error_title')
    )
    const computedErrorMessage = computed(
      () => props.errorMessage || t('MSelectInput.error_message')
    )
    return {
      computedErrorTitle,
      computedErrorMessage,
    }
  },
})
</script>

<style lang="scss">
@use '../../../sass/framework/mixins/font-size';
@use '../../../sass/framework/mixins/hover';
@use '../../../sass/framework/mixins/select-toggle';
@use '../../../sass/framework/mixins/input';

:root {
  --vs-colors--lightest: rgba(60, 60, 60, 0.26);
  --vs-colors--light: rgba(60, 60, 60, 0.5);
  --vs-colors--dark: #333;
  --vs-colors--darkest: rgba(0, 0, 0, 0.15);

  /* Search Input */
  --vs-search-input-color: var(--input-text-active);
  --vs-search-input-bg: var(--input-background);
  --vs-search-input-placeholder-color: var(--input-text-placeholder);

  /* Font */
  --vs-font-size: var(--typography-paragraph-regular-font-size);
  --vs-line-height: var(--typography-paragraph-regular-line-height);

  /* Disabled State */
  --vs-state-disabled-bg: var(--input-background-disabled);
  --vs-state-disabled-color: var(--input-text-disabled);
  --vs-state-disabled-controls-color: var(--input-text-disabled);
  --vs-state-disabled-cursor: not-allowed;
  --vs-disabled-bg: var(--vs-state-disabled-bg);
  --vs-disabled-color: var(--vs-state-disabled-color);
  --vs-disabled-cursor: var(--vs-state-disabled-cursor);

  /* Borders */
  --vs-border-color: var(--input-border);
  --vs-border-width: 1px;
  --vs-border-style: solid;
  --vs-border-radius: var(--input-border-radius);

  /* Actions: house the component controls */
  --vs-actions-padding: 0;

  /* Component Controls: Clear, Open Indicator */
  --vs-controls-color: var(--input-text-active);
  --vs-controls-size: 1;
  --vs-controls--deselect-text-shadow: 0 1px 0 #fff;

  /* Selected */
  --vs-selected-bg: var(--skin-background-hover-light);
  --vs-selected-color: var(--input-text-active);
  --vs-selected-border-color: var(--border-card);
  --vs-selected-border-style: solid;
  --vs-selected-border-width: 1px;

  /* Dropdown */
  --vs-dropdown-bg: var(--skin-background-light);
  --vs-dropdown-color: var(--skin-color-light);
  --vs-dropdown-z-index: 1000;
  --vs-dropdown-min-width: 160px;
  --vs-dropdown-max-height: 350px;
  --vs-dropdown-box-shadow: var(--shadow-low);

  /* Options */
  --vs-dropdown-option-bg: var(--skin-background-light);
  --vs-dropdown-option-color: var(--skin-color-light);
  --vs-dropdown-option-padding: var(--spacing-12) var(--spacing-16);

  /* Active State */
  --vs-dropdown-option--active-bg: var(--skin-background-hover-light);
  --vs-dropdown-option--active-color: var(--skin-color-light);

  /* Deselect State */
  --vs-dropdown-option--deselect-bg: var(--state-error-light);
  --vs-dropdown-option--deselect-color: var(--skin-color-light);

  /* Transitions */
  --vs-transition-timing-function: var(--animation-timing);
  --vs-transition-duration: 20s;
}

/*
Default styling
Importing it has caused issues with Nuxt Bridge in our move towards Vue 3, so as a workaround we just put the CSS in here for now.
Sort of hacky, but is only temporary until we upgrade Patterns and the select lib to become Vue 3 compatible..
 */
.v-select {
  font-family: inherit;
  position: relative;
}
.v-select,
.v-select * {
  box-sizing: border-box;
}
@-webkit-keyframes vSelectSpinner {
  0% {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(1turn);
  }
}
@keyframes vSelectSpinner {
  0% {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(1turn);
  }
}
.vs__fade-enter-active,
.vs__fade-leave-active {
  pointer-events: none;
  transition: opacity var(--vs-transition-duration)
    var(--vs-transition-timing-function);
}

.vs--disabled .vs__clear,
.vs--disabled .vs__dropdown-toggle,
.vs--disabled .vs__open-indicator,
.vs--disabled .vs__search,
.vs--disabled .vs__selected {
  background-color: var(--vs-disabled-bg);
  cursor: var(--vs-disabled-cursor);
}
.v-select[dir='rtl'] .vs__actions {
  padding: 0 3px 0 6px;
}
.v-select[dir='rtl'] .vs__clear {
  margin-left: 6px;
  margin-right: 0;
}
.v-select[dir='rtl'] .vs__deselect {
  margin-left: 0;
  margin-right: 2px;
}
.v-select[dir='rtl'] .vs__dropdown-menu {
  text-align: right;
}
.vs__dropdown-toggle {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  background: var(--vs-search-input-bg);
  border: var(--vs-border-width) var(--vs-border-style) var(--vs-border-color);
  border-radius: var(--vs-border-radius);
  display: flex;
  padding: 0 0 4px;
  white-space: normal;
}
.vs__selected-options {
  display: flex;
  flex-basis: 100%;
  flex-grow: 1;
  flex-wrap: wrap;
  padding: 0 2px;
  position: relative;
}
.vs__actions {
  align-items: center;
  display: flex;
  padding: var(--vs-actions-padding);
}
.vs--searchable .vs__dropdown-toggle {
  cursor: text;
}
.vs--unsearchable .vs__dropdown-toggle {
  cursor: pointer;
}
.vs--open .vs__dropdown-toggle {
  border-bottom-color: transparent;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
}
.vs__open-indicator {
  fill: var(--vs-controls-color);
  transform: scale(var(--vs-controls-size));
  transition: transform var(--vs-transition-duration)
    var(--vs-transition-timing-function);
  transition-timing-function: var(--vs-transition-timing-function);
}
.vs--open .vs__open-indicator {
  transform: rotate(180deg) scale(var(--vs-controls-size));
}
.vs--loading .vs__open-indicator {
  opacity: 0;
}
.vs__clear {
  background-color: transparent;
  border: 0;
  cursor: pointer;
  fill: var(--vs-controls-color);
  margin-right: 8px;
  padding: 0;
}
.vs__dropdown-menu {
  background: var(--vs-dropdown-bg);
  border: var(--vs-border-width) var(--vs-border-style) var(--vs-border-color);
  border-radius: 0 0 var(--vs-border-radius) var(--vs-border-radius);
  border-top-style: none;
  box-shadow: var(--vs-dropdown-box-shadow);
  box-sizing: border-box;
  color: var(--vs-dropdown-color);
  display: block;
  left: 0;
  list-style: none;
  margin: 0;
  max-height: var(--vs-dropdown-max-height);
  min-width: var(--vs-dropdown-min-width);
  overflow-y: auto;
  padding: 5px 0;
  position: absolute;
  text-align: left;
  top: calc(100% - var(--vs-border-width));
  width: 100%;
  z-index: var(--vs-dropdown-z-index);
}
.vs__no-options {
  text-align: center;
}
.vs__dropdown-option {
  clear: both;
  color: var(--vs-dropdown-option-color);
  cursor: pointer;
  display: block;
  line-height: 1.42857143;
  padding: var(--vs-dropdown-option-padding);
  white-space: nowrap;
}
.vs__dropdown-option--highlight {
  background: var(--vs-dropdown-option--active-bg);
  color: var(--vs-dropdown-option--active-color);
}
.vs__dropdown-option--deselect {
  background: var(--vs-dropdown-option--deselect-bg);
  color: var(--vs-dropdown-option--deselect-color);
}
.vs__dropdown-option--disabled {
  background: var(--vs-state-disabled-bg);
  color: var(--vs-state-disabled-color);
  cursor: var(--vs-state-disabled-cursor);
}
.vs__selected {
  align-items: center;
  background-color: var(--vs-selected-bg);
  border: var(--vs-selected-border-width) var(--vs-selected-border-style)
    var(--vs-selected-border-color);
  border-radius: var(--vs-border-radius);
  color: var(--vs-selected-color);
  display: flex;
  line-height: var(--vs-line-height);
  margin: 4px 2px 0;
  padding: 0 0.25em;
  z-index: 0;
}
.vs__deselect {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  background: none;
  border: 0;
  cursor: pointer;
  display: inline-flex;
  fill: var(--vs-controls-color);
  margin-left: 4px;
  padding: 0;
  text-shadow: var(--vs-controls--deselect-text-shadow);
}
.vs--single .vs__selected {
  background-color: transparent;
  border-color: transparent;
}
.vs--single.vs--loading .vs__selected,
.vs--single.vs--open .vs__selected {
  opacity: 0.4;
  position: absolute;
}
.vs--single.vs--searching .vs__selected {
  display: none;
}
.vs__search::-webkit-search-cancel-button {
  display: none;
}
.vs__search::-ms-clear,
.vs__search::-webkit-search-decoration,
.vs__search::-webkit-search-results-button,
.vs__search::-webkit-search-results-decoration {
  display: none;
}
.vs__search,
.vs__search:focus {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  background: none;
  border: 1px solid transparent;
  border-left: none;
  box-shadow: none;
  color: var(--vs-search-input-color);
  flex-grow: 1;
  font-size: var(--vs-font-size);
  line-height: var(--vs-line-height);
  margin: 4px 0 0;
  max-width: 100%;
  outline: none;
  padding: 0 7px;
  width: 0;
  z-index: 1;
}
.vs__search::-moz-placeholder {
  color: var(--vs-search-input-placeholder-color);
}
.vs__search:-ms-input-placeholder {
  color: var(--vs-search-input-placeholder-color);
}
.vs__search::placeholder {
  color: var(--vs-search-input-placeholder-color);
}
.vs--unsearchable .vs__search {
  opacity: 1;
}
.vs--unsearchable:not(.vs--disabled) .vs__search {
  cursor: pointer;
}
.vs--single.vs--searching:not(.vs--open):not(.vs--loading) .vs__search {
  opacity: 0.2;
}
.vs__spinner {
  align-self: center;
  -webkit-animation: vSelectSpinner 1.1s linear infinite;
  animation: vSelectSpinner 1.1s linear infinite;
  border: 0.9em solid hsla(0, 0%, 39%, 0.1);
  border-left-color: rgba(60, 60, 60, 0.45);
  font-size: 5px;
  opacity: 0;
  overflow: hidden;
  text-indent: -9999em;
  transform: translateZ(0)
    scale(var(--vs-controls--spinner-size, var(--vs-controls-size)));
  transition: opacity 0.1s;
}
.vs__spinner,
.vs__spinner::after {
  border-radius: 50%;
  height: 5em;
  transform: scale(var(--vs-controls--spinner-size, var(--vs-controls-size)));
  width: 5em;
}
.vs--loading .vs__spinner {
  opacity: 1;
}

// Custom styling
.m-select-input {
  &__loader {
    @keyframes loading-spin {
      0% {
        transform: rotate(0deg);
      }
      100% {
        transform: rotate(360deg);
      }
    }
    animation: loading-spin 2.5s infinite linear;
    line-height: 1;
    pointer-events: none;
  }

  &__error {
    background: var(--state-error-light);
    font-size: var(--typography-paragraph-small-font-size);
    margin: (-var(--spacing-16)) 0;
    padding: var(--spacing-16) var(--spacing-16);
    text-align: left;
  }
}

.vs,
[data-app='app-uk-quotebuy-cat-dog'] .m-select-input .vs {
  &__dropdown-toggle {
    @include input.input;
    display: flex;
    padding-bottom: 0;
    padding-top: 0;

    .vs--open & {
      border-radius: var(--input-border-radius);
    }

    // Match input error styling
    .m-form-group--error & {
      border-color: var(--state-error-dark);
    }
  }

  &__open-indicator {
    @include select-toggle.select-toggle-image;
    background-position: center center;
    height: 20px;
    transition: none;
    width: 20px;

    path {
      display: none;
    }
  }

  &__clear {
    display: none;
  }

  &__dropdown-menu {
    border: 0 none;
    border-radius: var(--input-border-radius);
    padding: var(--spacing-16) 0;
    top: 100%;
  }

  &__dropdown-option {
    @include font-size.font-size(sm);
    @include hover.hover-transition(background-color);
  }

  &__search[type='search'] {
    background: none;
    border: 0 none;
    margin: 0;
    padding: calc(var(--spacing-12) - 1px) 0; // Match input vertical padding
    width: 0;

    &:focus {
      background: none;
      border: 0 none;
      outline: none;
    }

    &[disabled] {
      border: 0 none;
    }
  }

  &__selected {
    // Matched input padding as if this was sat inside the input,
    // minus the border
    margin: calc(var(--spacing-12) - 2px) var(--spacing-16);
    margin-left: 0;

    // Prevent the selected text breaking onto two lines,
    // truncate instead so the select keeps looking like
    // a select and doesn't break
    &-options {
      flex-wrap: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .vs--single & {
      padding: 0;
    }
  }

  &__fade {
    &-enter-from,
    &-leave-to {
      opacity: 0;
      transform: translateY(-0.5em);
    }

    &-enter-active,
    &-leave-active {
      transition:
        transform var(--animation),
        opacity var(--animation);
    }
  }
}

.m-a11y-focus-outlines--visible .vs__dropdown-toggle:focus-within {
  // reinstate default focus shapes
  outline: 1px dotted #212121;
  outline: 5px auto -webkit-focus-ring-color;
}
</style>
